/**
 * TESTIMONIAL REQUESTS — SINGLE SOURCE OF TRUTH
 * Admin:  create / resend / list   (edge fn `testimonial-request`, admin JWT + MFA when enforced)
 * Public: open / submit by one-time token (no login). Submissions land as
 *         `pending` in mkt_testimonials; a DB trigger moves the request to
 *         approved / rejected when an admin moderates the review.
 */
import { supabase } from '@/lib/supabase';

export type RequestStatus = 'sent' | 'opened' | 'submitted' | 'approved' | 'rejected';
export type RequestChannel = 'email' | 'sms' | 'both' | 'link';

export const REQUEST_STATUSES: { key: RequestStatus; label: string; cls: string }[] = [
  { key: 'sent', label: 'Sent', cls: 'bg-sky-100 text-sky-800' },
  { key: 'opened', label: 'Opened', cls: 'bg-indigo-100 text-indigo-800' },
  { key: 'submitted', label: 'Submitted', cls: 'bg-orange-100 text-orange-800' },
  { key: 'approved', label: 'Approved', cls: 'bg-emerald-100 text-emerald-800' },
  { key: 'rejected', label: 'Rejected', cls: 'bg-red-100 text-red-700' },
];

export const CHANNELS: { key: RequestChannel; label: string }[] = [
  { key: 'email', label: 'Email' },
  { key: 'sms', label: 'SMS (GoHighLevel)' },
  { key: 'both', label: 'Email + SMS' },
  { key: 'link', label: 'Copy link only' },
];

/** Where a request came from. Auto requests are created by the daily `testimonial-auto-request` job. */
export type RequestSource = 'admin' | 'auto_first_lead_purchase' | 'auto_project_completed';

export const REQUEST_SOURCES: Record<RequestSource, { en: string; es: string }> = {
  admin: { en: 'Sent by admin', es: 'Enviada por admin' },
  auto_first_lead_purchase: { en: 'Auto · 7 days after first lead', es: 'Auto · 7 días tras primer lead' },
  auto_project_completed: { en: 'Auto · project completed', es: 'Auto · proyecto completado' },
};

export const statusMeta = (s: RequestStatus) => REQUEST_STATUSES.find((x) => x.key === s) || REQUEST_STATUSES[0];

export interface TestimonialRequest {
  id: string;
  token: string;
  email: string;
  phone?: string | null;
  name?: string | null;
  company_name?: string | null;
  user_id?: string | null;
  channel: RequestChannel;
  status: RequestStatus;
  send_count: number;
  last_sent_at: string;
  last_send_result?: Record<string, any>;
  opened_at?: string | null;
  submitted_at?: string | null;
  reviewed_at?: string | null;
  testimonial_id?: string | null;
  created_by_email?: string | null;
  created_at: string;
  source?: RequestSource;
  trigger_ref?: string | null;
}

const FN = 'testimonial-request';
const origin = () => (typeof window !== 'undefined' ? window.location.origin : 'https://giggone.com');

export const reviewLink = (token: string, lang: 'en' | 'es' = 'en') =>
  `${origin()}/${lang === 'es' ? 'es/resena' : 'en/review'}/${token}`;

const call = async (body: Record<string, unknown>) => {
  const { data, error } = await supabase.functions.invoke(FN, { body: { origin: origin(), ...body } });
  if (data?.error) return { error: String(data.reason || data.error), data };
  if (error) {
    let msg = error.message;
    try {
      const ctx = (error as any).context;
      if (ctx?.json) {
        const j = await ctx.json();
        msg = j?.reason || j?.error || msg;
      }
    } catch {
      /* ignore */
    }
    return { error: msg, data: null };
  }
  return { data };
};

export const listRequests = (status: RequestStatus | 'all' = 'all') => call({ action: 'list', status });
/** One customer's request history (Admin > Customers case view). Matches by user ID or email. */
export const listRequestsFor = (userId: string, email: string) => call({ action: 'list', status: 'all', userId, email });
export const createRequest = (p: { email: string; name?: string; company?: string; phone?: string; channel: RequestChannel; lang?: 'en' | 'es'; userId?: string }) =>
  call({ action: 'create', ...p });
export const resendRequest = (requestId: string, lang: 'en' | 'es' = 'en') => call({ action: 'resend', requestId, lang });
export const openRequest = (token: string) => call({ action: 'open', token });
export const submitRequest = (p: Record<string, unknown>) => call({ action: 'submit', ...p });

/** Resize a photo to max 1200px JPEG (keeps uploads small and fast on mobile). */
export const compressPhoto = (file: File, max = 1200): Promise<string> =>
  new Promise((resolve, reject) => {
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) return reject(new Error('Use a JPG, PNG or WebP image.'));
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const c = document.createElement('canvas');
      c.width = Math.round(img.width * scale);
      c.height = Math.round(img.height * scale);
      c.getContext('2d')?.drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url);
      resolve(c.toDataURL('image/jpeg', 0.82));
    };
    img.onerror = () => reject(new Error('Could not read that image.'));
    img.src = url;
  });
